/** The food database: search USDA + your own foods, inspect every nutrient, fill gaps with AI. */
import { useRef, useState, type ReactElement } from 'react';
import { AddToDiaryModal } from '../components/AddToDiaryModal';
import { CreateFoodModal } from '../components/CreateFoodModal';
import { FoodDetail } from '../components/FoodDetail';
import { FoodSearch } from '../components/FoodSearch';
import { Icon } from '../components/Icon';
import { Card, Empty, Segmented } from '../components/ui';
import { formatDateTime } from '../lib/dates';
import type { MealItem, MySubmission } from '../lib/types';
import { useApp, useAsync } from '../state/app';

type Scope = 'all' | 'mine';

export function FoodsPage(): ReactElement {
  const [selected, setSelected] = useState<number | null>(null);
  const [scope, setScope] = useState<Scope>('all');
  const [creating, setCreating] = useState<boolean>(false);
  const [adding, setAdding] = useState<MealItem | null>(null);
  const [refresh, setRefresh] = useState<number>(0);
  const detailRef = useRef<HTMLDivElement>(null);

  const pick = (id: number): void => {
    setSelected(id);
    // On phones the detail sits under the list: bring it into view.
    if (window.innerWidth < 1000) setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  return (
    <div className="foods-layout">
      <Card title="Find a food" right={<button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}><Icon name="plus" size={16} /> New food</button>}>
        <div className="stack" style={{ gap: 12 }}>
          <Segmented<Scope> options={[{ value: 'all', label: 'All foods' }, { value: 'mine', label: 'My foods' }]} value={scope} onChange={setScope} />
          <FoodSearch key={scope} mineOnly={scope === 'mine'} autoFocus onPick={(h) => pick(h.id)} selectedId={selected} refreshKey={refresh} />
        </div>
      </Card>
      <div ref={detailRef} style={{ scrollMarginTop: 80, minWidth: 0 }}><Card>
        {selected === null
          ? <><Empty title="Pick a food to see all 94 nutrients">USDA Foundation and SR Legacy foods are lab-measured. Anything missing can be estimated with AI and is clearly marked.</Empty><MySubmissions refreshKey={refresh} /></>
          : <FoodDetail foodId={selected} onAdd={(item) => setAdding(item)} onDeleted={() => { setSelected(null); setRefresh((r) => r + 1); }} />}
      </Card></div>
      {creating && <CreateFoodModal onClose={() => setCreating(false)} onCreated={(f) => { setScope('mine'); setSelected(f.id); setRefresh((r) => r + 1); }} />}
      {adding && <AddToDiaryModal item={adding} onClose={() => setAdding(null)} />}
    </div>
  );
}

const STATUS_TEXT: Readonly<Record<MySubmission['status'], string>> = { pending: 'waiting for review', approved: 'approved', rejected: 'not approved' };

/** What this user sent in (new products and fixes) and what became of it. */
function MySubmissions({ refreshKey }: { refreshKey: number }): ReactElement | null {
  const { source, version } = useApp();
  const mine = useAsync<MySubmission[]>(() => source.listMySubmissions(), [source, version, refreshKey]);
  const rows: MySubmission[] = mine.data ?? [];
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 18 }}>
      <div className="field-title">Products and fixes you sent in</div>
      <div className="stack" style={{ gap: 6 }}>
        {rows.slice(0, 20).map((r) => (
          <div key={r.id} className="row" style={{ gap: 8, fontSize: 13.5 }}>
            <span className={`chip s-${r.status}`}>{STATUS_TEXT[r.status]}</span>
            <span style={{ fontWeight: 700 }}>{r.kind === 'correction' ? 'Fix: ' : ''}{r.name}</span>
            <span className="faint" style={{ fontWeight: 600 }}>{formatDateTime(r.created_at)}{r.reviewer_note ? ` · "${r.reviewer_note}"` : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
